<template>
  <div class="toy-layout">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb" aria-label="Breadcrumb">
      <ol class="breadcrumb-list">
        <li class="breadcrumb-item">
          <router-link to="/">首页</router-link>
        </li>
        <li class="breadcrumb-separator">/</li>
        <li class="breadcrumb-item">
          <router-link :to="`/?category=${category}`">{{ categoryName }}</router-link>
        </li>
        <li class="breadcrumb-separator">/</li>
        <li class="breadcrumb-item active" aria-current="page">{{ title }}</li>
      </ol>
    </nav>

    <!-- 主内容区 -->
    <div class="toy-content">
      <!-- 左侧参数面板 -->
      <aside class="parameter-section">
        <slot name="parameters"></slot>
      </aside>
      
      <!-- 右侧可视化区域 -->
      <main class="visualization-section" :style="{'--viz-aspect': vizAspect}">
        <div class="viz-aspect">
          <slot name="visualization"></slot>
        </div>
        
        <!-- 操作栏 -->
        <div class="action-bar-container" v-if="$slots.actions">
          <slot name="actions"></slot>
        </div>
      </main>
    </div>
    
    <!-- 概念说明 -->
    <section class="concept-section">
      <FormulaBlock :html="conceptHtml" />
      <!-- 只有当 concept 中没有公式块时才单独渲染 formula -->
      <FormulaBlock v-if="formulaHtml && !hasFormulaInConcept" :html="formulaHtml" />
      <!-- 元信息：创建时间和修改时间 -->
      <div v-if="hasTimeInfo" class="toy-meta-info">
        <span v-if="formattedCreatedAt">{{ formattedCreatedAt }}</span>
        <span v-if="formattedUpdatedAt && formattedCreatedAt !== formattedUpdatedAt" class="meta-separator">·</span>
        <span v-if="formattedUpdatedAt && formattedCreatedAt !== formattedUpdatedAt">{{ formattedUpdatedAt }}</span>
      </div>
    </section>
    
    <!-- 代码展示 -->
    <section class="code-section">
      <slot name="code"></slot>
    </section>
  </div>
</template>

<script setup>
import FormulaBlock from './FormulaBlock.vue'
import { markdownToHtml } from '@/utils/markdown-enhanced.js'
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  category: {
    type: String,
    required: true
  },
  categoryName: {
    type: String,
    required: true
  },
  concept: {
    type: String,
    default: ''
  },
  formula: {
    type: String,
    default: ''
  },
  createdAt: {
    type: String,
    default: ''
  },
  updatedAt: {
    type: String,
    default: ''
  },
  vizAspect: {
    type: String,
    default: '4 / 3'
  },
  dataExplainer: {
    type: String,
    default: ''
  },
  methodExplainer: {
    type: String,
    default: ''
  },
  paramSensitivity: {
    type: String,
    default: ''
  },
  resultExplainer: {
    type: String,
    default: ''
  }
})

const conceptHtml = computed(() => {
  const s = typeof props.concept === 'string' ? props.concept : ''
  // 新方案会自动检测和处理所有 Markdown 语法，无需手动判断
  return markdownToHtml(s)
})

// 检查 concept 中是否已经包含公式块
const hasFormulaInConcept = computed(() => {
  const s = typeof props.concept === 'string' ? props.concept : ''
  // 检查是否包含 $$...$$ 块级公式
  return /\$\$[\s\S]*?\$\$/.test(s)
})

const formulaHtml = computed(() => {
  const f = typeof props.formula === 'string' ? props.formula : ''
  return f
})

// 格式化日期显示
function formatDate(dateStr) {
  if (!dateStr) return null
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return null
    // 格式化为 YYYY年MM月DD日
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}年${month}月${day}日`
  } catch {
    return null
  }
}

const formattedCreatedAt = computed(() => {
  if (!props.createdAt) return null
  const formatted = formatDate(props.createdAt)
  return formatted ? `创建于 ${formatted}` : null
})

const formattedUpdatedAt = computed(() => {
  if (!props.updatedAt) return null
  const formatted = formatDate(props.updatedAt)
  return formatted ? `最后更新于 ${formatted}` : null
})

const hasTimeInfo = computed(() => {
  return !!(formattedCreatedAt.value || formattedUpdatedAt.value)
})
</script>

<style scoped>
.toy-layout {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}

/* 面包屑导航 */
.breadcrumb {
  margin-bottom: 1.5rem;
}

.breadcrumb-list {
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.breadcrumb-item {
  font-size: 0.875rem;
  color: #666;
}

.breadcrumb-item a {
  color: #2196F3;
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-item a:hover {
  color: #1976D2;
  text-decoration: underline;
}

.breadcrumb-item.active {
  color: #333;
  font-weight: 500;
}

.breadcrumb-separator {
  color: #999;
  user-select: none;
}

/* 主内容区 */
.toy-content {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.parameter-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 1rem;
}

.visualization-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 0;
  height: fit-content;
}

.action-bar-container {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

/* 概念说明区域 */
.concept-section {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 表格样式 */
.concept-section table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
}

.concept-section th,
.concept-section td {
  padding: 0.75rem;
  text-align: left;
  border: 1px solid #e0e0e0;
}

.concept-section th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.concept-section tr:nth-child(even) {
  background-color: #fafafa;
}

/* 粗体样式 */
.concept-section strong {
  font-weight: 600;
  color: #333;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  margin-top: 1.5rem;
}

.section-title:first-child {
  margin-top: 0;
}

.concept-content,
.formula-content {
  color: #555;
  line-height: 1.6;
}

.formula-content {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  overflow-x: auto;
}

/* 元信息栏 */
.toy-meta-info {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  font-size: 0.875rem;
  color: #999;
  text-align: right;
}

.meta-separator {
  margin: 0 0.5rem;
  color: #ccc;
}

/* 代码展示区域 */
.code-section {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 响应式布局 - 平板设备 */
@media (max-width: 1024px) and (min-width: 769px) {
  .toy-content {
    grid-template-columns: 280px 1fr;
    gap: 1.25rem;
  }

  .parameter-section {
    padding: 1.25rem;
  }

  .visualization-section {
    padding: 1.25rem;
  }
}

/* 响应式布局 - 移动端垂直排列 */
@media (max-width: 768px) {
  .toy-layout {
    padding: 0.75rem;
  }

  .toy-content {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .parameter-section {
    position: static;
    padding: 1rem;
    order: 1;
  }

  .visualization-section {
    padding: 1rem;
    min-height: 350px;
    order: 2;
  }

  .concept-section,
  .code-section {
    padding: 1.5rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .breadcrumb-list {
    font-size: 0.8rem;
    gap: 0.375rem;
  }

  .action-bar-container {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
  }
}

/* 小屏幕移动设备 */
@media (max-width: 480px) {
  .toy-layout {
    padding: 0.5rem;
  }

  .toy-content {
    gap: 0.75rem;
  }

  .parameter-section,
  .visualization-section {
    padding: 0.75rem;
    border-radius: 6px;
  }

  .visualization-section {
    min-height: 300px;
  }

  .concept-section,
  .code-section {
    padding: 1rem;
    border-radius: 6px;
  }

  .section-title {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  .breadcrumb-list {
    font-size: 0.75rem;
  }

  .breadcrumb-item,
  .breadcrumb-separator {
    font-size: 0.75rem;
  }
}

/* 横屏模式优化 */
@media (max-width: 768px) and (orientation: landscape) {
  .toy-content {
    grid-template-columns: 300px 1fr;
    gap: 1rem;
  }

  .parameter-section {
    order: 1;
    max-height: 70vh;
    overflow-y: auto;
  }

  .visualization-section {
    order: 2;
    min-height: 300px;
  }
}

.viz-aspect {
  width: 100%;
  aspect-ratio: var(--viz-aspect);
}
</style>
